import { useState } from 'react';
import { Button, FieldError, Input, Label, Modal, TextField } from '@heroui/react';

import Notice from '../../common/Notice';
import PasswordField from '../../common/PasswordField';
import useValidation from '../../common/useValidation';
import * as check from '../../../services/validation';
import { requestEmailChange } from '../../../services/emailChange.service';
import { messageOf, EMAIL_CHANGE } from '../../../i18n/el';

/**
 * ΛΑ-02 / **D45**: ο διάλογος του αιτήματος αλλαγής διεύθυνσης.
 *
 * ⚠ **Ο ΚΩΔΙΚΟΣ ΖΗΤΕΙΤΑΙ, ΚΑΙ Ο ΛΟΓΟΣ ΓΡΑΦΕΤΑΙ ΣΤΗΝ ΟΘΟΝΗ.** Η διεύθυνση δεν
 * είναι πεδίο προφίλ: είναι το **αναγνωριστικό σύνδεσης** και ο προορισμός της
 * ανάκτησης πρόσβασης. Χωρίς κωδικό, μία κλεμμένη συνεδρία θα μετέφερε τον
 * λογαριασμό σε ξένο γραμματοκιβώτιο με ένα πάτημα — και από εκεί η επαναφορά
 * κωδικού ολοκληρώνει την κατάληψη χωρίς να χρειαστεί ποτέ ο κωδικός.
 *
 * Η επεξήγηση δεν είναι διακοσμητική: πεδίο κωδικού που εμφανίζεται χωρίς λόγο
 * διαβάζεται ως δυσπιστία, και ο χρήστης που δεν καταλαβαίνει γιατί του ζητείται
 * κάτι το δίνει πιο πρόχειρα.
 *
 * **Σύμβαση D26:** το `isOpen` δίνεται **σκέτο** και ο γονέας αποδίδει τον
 * διάλογο **υπό συνθήκη**. Κάθε πηγή κλεισίματος που δεν ξεκινά από τη
 * βιβλιοθήκη —επιτυχής αποστολή, κουμπί «Άκυρο»— αλλάζει το prop του γονέα.
 */
export default function EmailChangeDialog({ currentEmail, onClose, onRequested }) {
    const [newEmail, setNewEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isPending, setIsPending] = useState(false);
    const [error, setError] = useState('');

    const form = useValidation();

    // Ο ίδιος τοπικός έλεγχος με τις υπόλοιπες φόρμες. Ο διακομιστής παραμένει ο
    // κύριος: εδώ κρίνεται μόνο η **μορφή**, ποτέ η διαθεσιμότητα.
    const localEmail = check.email(newEmail)
        || (newEmail.trim().toLowerCase() === String(currentEmail ?? '').toLowerCase()
            ? 'Η νέα διεύθυνση είναι ίδια με την τρέχουσα.'
            : '');
    const emailMessage = form.show('newEmail', localEmail);

    function handleSubmit(event) {
        event.preventDefault();
        form.markSubmitted();
        setError('');

        if (localEmail || !password) return;

        setIsPending(true);
        requestEmailChange(newEmail.trim().toLowerCase(), password).then(
            (response) => {
                setIsPending(false);
                // Ο γονέας κλείνει τον διάλογο και δείχνει την εκκρεμότητα: η
                // μασκαρισμένη διεύθυνση έρχεται από τον **εξυπηρετητή**, ώστε
                // η μάσκα να μην έχει δεύτερη υλοποίηση στον πελάτη.
                onRequested(response?.pendingEmail ?? null);
            },
            (apiError) => {
                setError(messageOf(apiError));
                setIsPending(false);
            }
        );
    }

    return (
        <Modal.Root isOpen onOpenChange={(open) => { if (!open) onClose(); }}>
            <Modal.Backdrop>
                <Modal.Container>
                    <Modal.Dialog className="w-full sm:min-w-[36vw] sm:max-w-xl">
                        <Modal.Header>
                            <Modal.Heading>{EMAIL_CHANGE.dialogTitle}</Modal.Heading>
                        </Modal.Header>

                        <form onSubmit={handleSubmit} noValidate>
                            <Modal.Body className="flex flex-col gap-4">
                                {error && (
                                    <Notice status="danger" title={EMAIL_CHANGE.dialogTitle}>
                                        {error}
                                    </Notice>
                                )}

                                <p className="text-sm text-muted">{EMAIL_CHANGE.dialogHint}</p>

                                <TextField
                                    value={newEmail}
                                    onChange={(value) => setNewEmail(
                                        typeof value === 'string' ? value : value?.target?.value ?? ''
                                    )}
                                    onBlur={form.touch('newEmail')}
                                    isInvalid={Boolean(emailMessage)}
                                    isRequired
                                    autoFocus
                                >
                                    <Label>{EMAIL_CHANGE.newEmail}</Label>
                                    <Input type="email" autoComplete="email" />
                                    <FieldError>{emailMessage}</FieldError>
                                </TextField>

                                <div className="flex flex-col gap-1">
                                    <PasswordField
                                        label={EMAIL_CHANGE.currentPassword}
                                        name="currentPassword"
                                        value={password}
                                        onChange={setPassword}
                                        autoComplete="current-password"
                                    />
                                    <span className="text-sm text-muted">
                                        {EMAIL_CHANGE.passwordHint}
                                    </span>
                                </div>
                            </Modal.Body>

                            <Modal.Footer>
                                <Button variant="outline" onPress={onClose}>
                                    {EMAIL_CHANGE.cancel}
                                </Button>
                                <Button type="submit" isPending={isPending}>
                                    {EMAIL_CHANGE.submit}
                                </Button>
                            </Modal.Footer>
                        </form>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </Modal.Root>
    );
}
